import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { EventNote as ScheduleIcon } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { format, parseISO } from 'date-fns';
import { functions } from '../../../shared/utils/firebase';
import { subscribeToCoachSchedulesMeta } from '../../coachSchedules/coachSchedulesService';
import { CoachSchedulesMeta } from '../../coachSchedules/types';

interface SyncResult {
  status: 'ok' | 'partial' | 'error' | 'locked';
  written: number;
  deleted: number;
  unchanged: number;
  error?: string | null;
}

interface Outcome {
  severity: 'success' | 'warning' | 'error';
  message: string;
}

/** Manual runs make one attempt (no retry wait), so this covers the slowest normal run with room to spare. */
const CALL_TIMEOUT_MS = 300_000;

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

/**
 * Admin Panel → Data Sync. Shows when each coach-schedule date was last checked against Mindbody and
 * runs the same Cloud Function the 05:00 / 12:00 schedule uses ("Sync now"), optionally re-fetching
 * session-type and location names ("Refresh names").
 */
const CoachSchedulesSyncCard: React.FC = () => {
  const [meta, setMeta] = useState<CoachSchedulesMeta | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [running, setRunning] = useState<'sync' | 'names' | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToCoachSchedulesMeta(setMeta, (error) => setMetaError(error.message));
    return () => unsubscribe();
  }, []);

  const handleRun = async (kind: 'sync' | 'names') => {
    setRunning(kind);
    setOutcome(null);
    try {
      const trigger = httpsCallable<{ refreshLookups?: boolean }, SyncResult>(functions, 'triggerCoachScheduleSync', {
        timeout: CALL_TIMEOUT_MS,
      });
      const { data } = await trigger(kind === 'names' ? { refreshLookups: true } : {});
      if (data.status === 'ok') {
        setOutcome({
          severity: 'success',
          message: `Sync finished: ${plural(data.written, 'coach day')} updated, ${data.deleted} removed, ${data.unchanged} unchanged`,
        });
      } else {
        setOutcome({ severity: 'warning', message: data.error || 'The sync did not finish.' });
      }
    } catch (error) {
      console.error('[coach-schedules] manual sync failed', error);
      setOutcome({ severity: 'error', message: error instanceof Error ? error.message : 'Sync failed. Check the function logs.' });
    } finally {
      setRunning(null);
    }
  };

  // The data's age is the last run that synced a date; a failed run leaves the previous documents in place.
  const statusChip = meta?.last_success_at ? (
    <Chip size="small" color={meta.status === 'ok' ? 'success' : 'warning'} label={`Synced ${format(meta.last_success_at, 'EEE d MMM, HH:mm')}`} />
  ) : (
    <Chip size="small" label="Never synced" />
  );
  const dates = Object.entries(meta?.dates ?? {}).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Card>
      <CardHeader
        avatar={<ScheduleIcon />}
        title="Coach schedules"
        subheader="Today's and tomorrow's Mindbody appointments for each coach, for the dashboard's Today view"
        action={
          <Stack direction="row" spacing={1} sx={{ mt: 1, mr: 1 }}>
            <Button
              variant="outlined"
              onClick={() => handleRun('names')}
              disabled={running !== null}
              aria-busy={running === 'names'}
              startIcon={running === 'names' ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Refresh names
            </Button>
            <Button
              variant="contained"
              onClick={() => handleRun('sync')}
              disabled={running !== null}
              aria-busy={running === 'sync'}
              startIcon={running === 'sync' ? <CircularProgress size={16} color="inherit" /> : <ScheduleIcon />}
            >
              Sync now
            </Button>
          </Stack>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {statusChip}
            {dates.map(([date, state]) => (
              <Typography key={date} variant="body2" color={state.status === 'skipped' ? 'warning.main' : 'text.secondary'}>
                {`${format(parseISO(date), 'EEE d MMM')}: ${plural(state.sessions, 'session')} · ${plural(state.staff, 'coach', 'coaches')}`}
                {state.status === 'skipped' ? ' · skipped' : ''}
              </Typography>
            ))}
          </Stack>

          {metaError ? <Alert severity="error">{metaError}</Alert> : null}
          {/* After a manual run its own outcome says the same thing, so the stored error isn't repeated. */}
          {meta && meta.status !== 'ok' && meta.error && !outcome ? <Alert severity="warning">{meta.error}</Alert> : null}
          {outcome ? <Alert severity={outcome.severity}>{outcome.message}</Alert> : null}

          <Typography variant="caption" color="text.secondary">
            Runs at 05:00 and 12:00 Singapore time.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export { CoachSchedulesSyncCard };
export default CoachSchedulesSyncCard;
